/**
 * Checkout.
 *
 * The shape of this view is set by what core actually does. `StartCheckout`
 * reserves each item, writes a quote in `TAX_PENDING`, and queues a tax job;
 * the tax worker's result creates the order and queues the PayPal create; only
 * then is there something to approve. So the customer's journey is: one
 * deliberate form, then a *watched* server-side sequence with a real
 * reservation timer, then a handoff.
 *
 * Rules that are load-bearing here rather than decorative:
 *
 * - **No money is computed on this page.** Every figure comes from core's quote.
 *   `checkItemisedTotal` re-adds core's own parts and blocks the handoff if
 *   they disagree with core's own total, which is the only arithmetic here and
 *   it exists to refuse a number, never to produce one.
 * - **One command id per intent.** A double-click reuses it, so core replays;
 *   editing the address mints a new one, so core does not reject the edit as an
 *   idempotency conflict.
 * - **The address is never patched.** It is sent as typed. If PayPal's
 *   authorised address does not match, core holds the order and this surface
 *   says so — it does not quietly substitute one address for another.
 * - **The reservation timer is honest.** It counts down from core's
 *   `expires_at`, and on expiry it does not vanish: it becomes an explanation
 *   and a way to start again (Design Bible 11.6).
 */

import { type FormEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { useStorefront } from "../app/StorefrontContext.js";
import { useAnnounce } from "../components/Announcer.js";
import { InternalLink } from "../components/InternalLink.js";
import { CheckoutCopy, PurchaseProgress, SelectedCopies } from "../components/PurchaseSummary.js";
import { ActionButton, Money, StatusChip } from "../components/primitives.js";
import {
  CheckoutUnavailableBlock,
  EmptyBlock,
  ErrorBlock,
  LoadingBlock
} from "../components/states.js";
import {
  EMPTY_ADDRESS,
  emailProblem,
  hasProblems,
  toCommandAddress,
  validateAddress,
  type AddressDraft
} from "../lib/address.js";
import { commandID, startCheckout } from "../lib/commands.js";
import type { ShippingOption } from "../lib/config.js";
import { itemPath } from "../lib/coreRoutes.js";
import { countdownSentence, countdownTo } from "../lib/format.js";
import { checkoutStage, type CheckoutStage } from "../lib/labels.js";
import { checkItemisedTotal } from "../lib/money.js";
import {
  parsePublicItem,
  type CheckoutStarted,
  type CheckoutState,
  type PublicItem
} from "../lib/publicContract.js";
import { correlationID, interpret } from "../lib/transport.js";
import { useCheckoutStatus } from "../lib/useCheckoutStatus.js";
import { AddressFields } from "./AddressFields.js";

/**
 * Joins cart ids into one comparable string. ASCII unit separator: it cannot
 * occur in a PocketBase id, so the joined string is a faithful identity for the
 * cart's *contents* rather than for the array object that happens to hold them.
 */
const CART_SEPARATOR = String.fromCodePoint(31);

type Stage =
  | { readonly kind: "form" }
  | { readonly kind: "starting" }
  | { readonly kind: "started"; readonly started: CheckoutStarted }
  | { readonly kind: "refused"; readonly title: string; readonly detail: string };

export function CheckoutView(): ReactNode {
  const { config, cart, handoff } = useStorefront();

  if (!config.checkout_enabled) {
    return (
      <div className="stack">
        <h1 className="t-display-l">Checkout</h1>
        <CheckoutUnavailableBlock reason={config.problem ?? "Checkout is switched off."} />
      </div>
    );
  }

  if (cart.length === 0 && handoff.quoteID === null) {
    return (
      <div className="stack">
        <h1 className="t-display-l">Checkout</h1>
        <EmptyBlock
          action={
            <a className="btn btn--secondary" href="/catalog">
              Browse the catalogue
            </a>
          }
          statement="There is nothing to check out. Your cart is empty."
        />
      </div>
    );
  }

  return <CheckoutCartGuard itemIDs={cart} key={cart.join(CART_SEPARATOR)} />;
}

type CartGate =
  | { readonly kind: "checking" }
  | { readonly kind: "ready" }
  | { readonly kind: "blocked"; readonly item: PublicItem }
  | { readonly kind: "failed" };

/**
 * A tab can restore an old cart after an item has moved to eBay. The item page
 * already has no website commerce path in that state; this guard makes the
 * same promise for restored state by re-reading every exact item before the
 * checkout form exists. Only public catalogue reads happen while checking.
 *
 * Two rules bound *when* it may read, and both are load-bearing:
 *
 * - **Once core holds a reservation, core is the authority, not the public
 *   catalogue.** A reserved object leaves that catalogue, so the read 404s and
 *   this guard would tell a customer who has a live reservation *and* a live
 *   order that "no reservation or payment request was sent". That is false.
 *   From the moment the tab holds a quote id, `CheckoutFlow` shows core's own
 *   authoritative state and this guard stands aside.
 * - **The check is keyed on cart contents, never on array identity.**
 *   `rememberCheckout` rewrites the tab with `[...cart]` — a new array with
 *   identical contents — so keying the effect on that array re-ran it after
 *   every successful checkout start. A genuinely different cart still
 *   re-checks: the contents change, and `CheckoutView` keys this component on
 *   the same string.
 */
function CheckoutCartGuard({ itemIDs }: { itemIDs: readonly string[] }): ReactNode {
  const { navigate, transport, handoff, rememberItem } = useStorefront();
  const started = handoff.quoteID !== null;
  const [checked, setChecked] = useState<CartGate>({ kind: "checking" });
  // Derived, not written from the effect: a started checkout is past this gate
  // by definition, so there is nothing to wait for and nothing to set.
  const gate: CartGate = started ? { kind: "ready" } : checked;
  const cartKey = itemIDs.join(CART_SEPARATOR);

  useEffect(() => {
    if (started) return;
    const abort = new AbortController();
    void Promise.all(
      cartKey.split(CART_SEPARATOR).map(async (itemID) => {
        const reply = await transport({
          path: itemPath(itemID),
          method: "GET",
          correlationID: correlationID(),
          signal: abort.signal
        });
        return interpret(reply, parsePublicItem, "an exact public item");
      })
    ).then((outcomes) => {
      if (abort.signal.aborted) return;
      const failed = outcomes.find((outcome) => outcome.kind !== "ok");
      if (failed !== undefined) {
        setChecked({ kind: "failed" });
        return;
      }
      const items = outcomes.map((outcome) => {
        if (outcome.kind !== "ok") throw new Error("unreachable cart contract outcome");
        return outcome.value;
      });
      const blocked = items.find(
        (item) => item.availability === "ACTIVE_EBAY" || !item.checkout_eligible
      );
      items.forEach(rememberItem);
      setChecked(blocked === undefined ? { kind: "ready" } : { kind: "blocked", item: blocked });
    });
    return () => {
      abort.abort();
    };
  }, [cartKey, started, transport, rememberItem]);

  if (gate.kind === "checking") {
    return (
      <div className="stack">
        <h1 className="t-display-l">Checkout</h1>
        <LoadingBlock cards={1} waitingFor="the current sales channel for each item" />
      </div>
    );
  }
  if (gate.kind === "failed") {
    return (
      <div className="stack">
        <h1 className="t-display-l">Checkout</h1>
        <ErrorBlock
          detail="The current sales channel for every item could not be verified. No reservation or payment request was sent. Return to your cart and try again when the catalogue is available."
          title="Checkout cannot verify these items."
        >
          <InternalLink className="btn btn--secondary" href="/cart">Review your cart</InternalLink>
        </ErrorBlock>
      </div>
    );
  }
  if (gate.kind === "blocked") {
    return (
      <div className="stack">
        <h1 className="t-display-l">Checkout</h1>
        <div className="state state--stop" role="alert">
          <p className="state-title t-body-l">This exact item is no longer sold on this site.</p>
          <p className="state-detail">
            SKU <span className="t-id">{gate.item.sku}</span>{" "}
            {gate.item.availability === "ACTIVE_EBAY" ? "is now listed on eBay." : "is not available for direct checkout."}{" "}
            No reservation or payment request was sent. Review your cart to remove it and continue with the rest.
          </p>
          <div className="state-actions">
            <button
              className="btn btn--secondary"
              onClick={() => {
                navigate("/cart");
              }}
              type="button"
            >
              Review your cart
            </button>
          </div>
        </div>
      </div>
    );
  }
  return <CheckoutFlow />;
}

function CheckoutFlow(): ReactNode {
  const { config, cart, transport, csrfToken, handoff, rememberCheckout } = useStorefront();
  const announce = useAnnounce();

  const firstOption = config.shipping_options[0];
  const [stage, setStage] = useState<Stage>({ kind: "form" });
  const [address, setAddress] = useState<AddressDraft>(EMPTY_ADDRESS);
  const [email, setEmail] = useState("");
  const [shippingOptionID, setShippingOptionID] = useState(firstOption?.id ?? "");
  const [showProblems, setShowProblems] = useState(false);
  const pendingCommandID = useRef<string | null>(null);

  const signedIn = csrfToken !== null;
  const addressProblems = validateAddress(address);
  const emailIssue = signedIn ? null : emailProblem(email);
  const option = config.shipping_options.find((entry) => entry.id === shippingOptionID);

  const quoteID = stage.kind === "started" ? stage.started.quote_id : handoff.quoteID;
  const checkoutToken =
    stage.kind === "started" ? stage.started.checkout_token : (handoff.checkoutToken ?? undefined);

  const status = useCheckoutStatus(transport, {
    quoteID,
    ...(checkoutToken === undefined || checkoutToken === null ? {} : { checkoutToken }),
    ...(csrfToken === null ? {} : { csrfToken }),
    enabled: quoteID !== null
  });

  // Any edit invalidates the pending command id, so core sees a new intent
  // rather than an idempotency conflict against the previous body.
  const invalidate = (): void => {
    pendingCommandID.current = null;
  };

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setShowProblems(true);
    if (emailIssue !== null || hasProblems(addressProblems)) {
      announce("Some details still need fixing before checkout can start.");
      const firstProblem = emailIssue !== null ? "checkout-email" : `address-${Object.keys(addressProblems)[0] ?? "recipientName"}`;
      event.currentTarget.querySelector<HTMLInputElement>(`#${firstProblem}`)?.focus();
      return;
    }
    if (option === undefined) {
      announce("Choose a shipping option before continuing.");
      return;
    }
    if (stage.kind === "starting") return;

    pendingCommandID.current ??= commandID();
    const id = pendingCommandID.current;
    setStage({ kind: "starting" });
    announce("Reserving your items and working out the total.");

    void startCheckout(
      transport,
      {
        commandID: id,
        itemIDs: cart,
        ...(signedIn ? {} : { guestEmail: email.trim() }),
        shippingOptionID: option.id,
        shippingMinor: option.shipping_minor,
        insuranceMinor: option.insurance_minor,
        address: toCommandAddress(address)
      },
      csrfToken ?? undefined
    ).then((outcome) => {
      if (outcome.kind === "ok") {
        rememberCheckout({
          quoteID: outcome.value.quote_id,
          orderID: outcome.value.order_id ?? null,
          checkoutToken: outcome.value.checkout_token ?? null
        });
        setStage({ kind: "started", started: outcome.value });
        announce("Your items are reserved. The server is working out tax.");
        return;
      }
      setStage(refusal(outcome.kind, outcome));
      announce(outcome.kind === "offline" ? "The checkout response was not received. Recovery instructions are on screen." : "Checkout could not be confirmed. The reason is on screen.");
    });
  };

  if (stage.kind === "refused") {
    return (
      <div className="stack">
        <h1 className="t-display-l">Checkout</h1>
        <ErrorBlock
          detail={stage.detail}
          onRetry={() => {
            /*
             * The command id is deliberately *not* cleared here. Going back to
             * an unchanged form and submitting again is the same intent, so
             * core replays its stored result instead of opening a second quote
             * and a second reservation. Editing any field clears it.
             */
            setStage({ kind: "form" });
          }}
          retryLabel="Back to your details"
          title={stage.title}
        />
      </div>
    );
  }

  if (quoteID !== null) {
    return (
      <div className="stack checkout">
        <h1 className="t-display-l">Checkout</h1>
        <AuthoritativeCheckout
          onStartOver={() => {
            pendingCommandID.current = null;
            /*
             * The tab's quote id has to go too. `quoteID` falls back to it, so
             * clearing only the local stage left "Start again" showing the same
             * expired quote — a control that did nothing. The cart is kept: the
             * customer wants these objects, they just lost the hold on them.
             */
            rememberCheckout({ quoteID: null, orderID: null, checkoutToken: null });
            setStage({ kind: "form" });
          }}
          status={status}
        />
      </div>
    );
  }

  return (
    <div className="stack checkout">
      <div className="section-head">
        <h1 className="t-display-l">Checkout</h1>
        <InternalLink href="/cart">Review your cart</InternalLink>
      </div>
      <p className="t-body-l">
        {cart.length === 1
          ? "One exact copy."
          : `${String(cart.length)} exact copies.`}{" "}
        Add your delivery details, review the total, then approve payment on PayPal.
      </p>
      <PurchaseProgress current="Delivery" />

      {!signedIn || config.collector_tier === null ? null : (
        <div className="banner banner--accent">
          <StatusChip label="Collector tier" tone="accent" />
          <span>
            Your account is on the <strong>{config.collector_tier.label}</strong> tier. The server
            applies it from its own records when it prices this order; nothing on this page can
            change it.
          </span>
        </div>
      )}

      <div className="checkout-layout">
      <SelectedCopies />
      <form className="form checkout-form" noValidate onSubmit={submit}>
        <fieldset className="checkout-fields" disabled={stage.kind === "starting"}>
          <legend className="u-visually-hidden">Contact, delivery, and shipping</legend>
          {signedIn ? (
            <p className="t-body-s t-secondary">
              You are signed in. Your receipt goes to the address on your account, which is why this
              form does not ask for it again.
            </p>
          ) : (
            <div className="field">
              <label className="t-label" htmlFor="checkout-email">
                Email address for your receipt
              </label>
              <input
                autoComplete="email"
                {...(showProblems && emailIssue !== null
                  ? { "aria-describedby": "checkout-email-problem", "aria-invalid": true }
                  : {})}
                id="checkout-email"
                inputMode="email"
                maxLength={254}
                name="email"
                required
                onChange={(event) => {
                  setEmail(event.target.value);
                  invalidate();
                }}
                type="email"
                value={email}
              />
              {showProblems && emailIssue !== null ? (
                <p className="field-problem" id="checkout-email-problem">
                  {emailIssue}
                </p>
              ) : null}
            </div>
          )}

          <AddressFields
            draft={address}
            onChange={(next) => {
              setAddress(next);
              invalidate();
            }}
            problems={showProblems ? addressProblems : {}}
          />

          <ShippingChoice
            onChange={(next) => {
              setShippingOptionID(next);
              invalidate();
            }}
            options={config.shipping_options}
            selected={shippingOptionID}
          />

          <ActionButton
            id="start-checkout"
            pending={stage.kind === "starting"}
            pendingLabel="Reserving your items…"
            type="submit"
          >
            Reserve and price this order
          </ActionButton>

          <p className="t-body-s t-secondary">
            Nothing is charged at this step. You will see shipping, tax, and the full total before payment.
          </p>
          <p className="t-body-s t-secondary">{config.legal.returns}</p>
          <p className="t-body-s t-secondary">{config.legal.shipping}</p>
        </fieldset>
      </form>
      </div>
    </div>
  );
}

function refusal(kind: string, outcome: { readonly kind: string }): Stage {
  if (kind === "conflict") {
    return {
      kind: "refused",
      title: "One of these objects is no longer available.",
      detail:
        "Availability changed while you were filling this in. Return to your cart to check each copy before continuing."
    };
  }
  if (kind === "disabled") {
    return {
      kind: "refused",
      title: "Checkout is switched off on this site.",
      detail:
        "Direct checkout is not available yet. Nothing was sent to a payment provider. You can still ask about this item."
    };
  }
  if (kind === "offline") {
    return {
      kind: "refused",
      title: "Checkout response was not received.",
      detail:
        "The connection dropped before checkout was confirmed. Your request may have reached us. Return to your details and submit them unchanged to safely check the same request."
    };
  }
  if (kind === "denied") {
    return {
      kind: "refused",
      title: "This checkout needs a valid session.",
      detail:
        "Your sign-in expired while you were filling this in. Sign in again to check your checkout status before continuing."
    };
  }
  const detail =
    "detail" in outcome && typeof (outcome as { detail?: unknown }).detail === "string"
      ? (outcome as { detail: string }).detail
      : "The server did not state a reason.";
  return {
    kind: "refused",
    title: "The server refused this checkout.",
    detail: `${detail} Payment has not been confirmed. Return to your details to check the same request.`
  };
}

function ShippingChoice({
  options,
  selected,
  onChange
}: {
  options: readonly ShippingOption[];
  selected: string;
  onChange: (id: string) => void;
}): ReactNode {
  return (
    <fieldset className="fieldset">
      <legend className="t-label">Shipping</legend>
      {options.map((option) => (
        <div className="radio" key={option.id}>
          <input
            checked={option.id === selected}
            id={`shipping-${option.id}`}
            name="shipping"
            onChange={() => {
              onChange(option.id);
            }}
            type="radio"
            value={option.id}
          />
          <label htmlFor={`shipping-${option.id}`}>
            <span className="t-body-m">{option.label}</span>{" "}
            <Money minor={option.shipping_minor} size="s" />
            <span className="t-body-s t-secondary"> {option.detail}</span>
            {option.insurance_minor > 0 ? (
              <span className="t-body-s t-secondary">
                {" "}
                Insurance <Money minor={option.insurance_minor} size="s" />.
              </span>
            ) : null}
          </label>
        </div>
      ))}
    </fieldset>
  );
}

/**
 * Everything after the form: the watched sequence, the reservation timer, the
 * itemised total, and the handoff. This is the part that must never claim more
 * than core told it.
 */
function AuthoritativeCheckout({
  status,
  onStartOver
}: {
  status: ReturnType<typeof useCheckoutStatus>;
  onStartOver: () => void;
}): ReactNode {
  const announce = useAnnounce();
  const { phase } = status;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => { setNow(Date.now()); }, 1_000);
    return () => { clearInterval(timer); };
  }, []);

  useEffect(() => {
    if (phase.kind === "ready") announce(checkoutPresentation(phase.state).title);
  }, [phase, announce]);

  if (phase.kind === "idle" || phase.kind === "loading") {
    return <LoadingBlock cards={1} waitingFor="your reserved items and the final total" />;
  }

  if (phase.kind === "failed") {
    return (
      <ErrorBlock
        detail={
          phase.outcome.kind === "not-found"
            ? "This checkout could not be found with this browser's reference. Payment cannot be confirmed here. Do not pay again; ask for help with your order."
            : "The current status could not be loaded. Do not pay again. Check the order status to retrieve any payment you have already approved."
        }
        onRetry={status.refresh}
        retryLabel="Check order status"
        title="The state of this order could not be read."
      />
    );
  }

  const state = phase.state;
  const stage = checkoutPresentation(state);
  const deadlinePassed = countdownTo(state.expires_at, now)?.expired === true;

  if (state.status === "EXPIRED" || state.status === "CANCELLED") {
    return (
      <ErrorBlock
        detail={`${stage.detail} Starting again from your cart re-reserves anything still available.`}
        onRetry={onStartOver}
        retryLabel="Start again"
        title={stage.title}
      />
    );
  }

  return (
    <div className="stack">
      <PurchaseProgress current={stage.currentStep} />
      {deadlinePassed && !stage.paid ? null : <CheckoutStageBanner state={state} />}
      {stage.paid ? null : <ReservationTimer expiresAt={state.expires_at} now={now} onRefresh={status.refresh} />}
      <QuoteSummary state={state} />
      {!stage.paid && !deadlinePassed ? <div className={phase.kind === "exhausted" ? "state state--accent" : "actions"}>
        {phase.kind === "exhausted" ? <>
          <p className="state-title">Automatic status checking paused.</p>
          <p className="state-detail">The latest result is shown above. Check status to request an update. If you already approved payment, do not pay again.</p>
        </> : null}
        <button className="btn btn--secondary" type="button" onClick={status.refresh}>Check status</button>
      </div> : null}
      {deadlinePassed && !stage.paid ? null : <PayPalHandoff state={state} />}
    </div>
  );
}

function CheckoutStageBanner({ state }: { state: CheckoutState }): ReactNode {
  const stage = checkoutPresentation(state);
  return (
    <div className={stage.awaitingAuthority ? "banner banner--accent" : "banner"} role="status">
      <StatusChip label={stage.title} tone={stage.paid ? "ok" : "accent"} />
      <span>{stage.detail}</span>
    </div>
  );
}

/** Design Bible 11.6. On expiry it explains rather than disappearing. */
function ReservationTimer({
  expiresAt,
  now,
  onRefresh
}: {
  expiresAt: string;
  now: number;
  onRefresh: () => void;
}): ReactNode {
  const announce = useAnnounce();

  const countdown = countdownTo(expiresAt, now);
  useEffect(() => {
    if (countdown !== null && countdown.totalSeconds % 60 === 0) {
      announce(countdownSentence(countdown));
    }
  }, [countdown, announce]);

  if (countdown === null) return null;

  if (countdown.expired) {
    return (
      <div className="state state--stop" role="alert">
        <p className="state-title t-body-l">
          <StatusChip label="Check reservation" tone="stop" />
          The reservation timer has ended.
        </p>
        <p className="state-detail">
          Check the latest status before continuing. If you already approved payment, do not pay
          again. We need confirmation before releasing this checkout or starting another.
        </p>
        <div className="state-actions">
          <button className="btn btn--secondary" onClick={onRefresh} type="button">
            Check reservation status
          </button>
        </div>
      </div>
    );
  }

  const urgency = countdown.totalSeconds <= 30 ? "timer--stop" : countdown.totalSeconds <= 120 ? "timer--accent" : "";

  return (
    <p className={`timer ${urgency}`}>
      <span className="t-label">Reserved for you</span>{" "}
      <span className="t-id t-id--m">{countdown.display}</span>
      <span className="u-visually-hidden"> — {countdownSentence(countdown)}</span>
    </p>
  );
}

function QuoteSummary({ state }: { state: CheckoutState }): ReactNode {
  const check = checkItemisedTotal(state);
  return (
    <section aria-labelledby="summary-title" className="summary">
      <h2 className="t-display-xs" id="summary-title">
        What you are paying for
      </h2>
      <ul className="summary-lines">
        {state.lines.map((line) => (
          <li key={line.physical_item_id}>
            <CheckoutCopy line={line} />
            <dl className="summary-line-money">
              <div>
                <dt className="t-label">Price</dt>
                <dd>
                  <Money minor={line.base_price_minor} size="s" />
                </dd>
              </div>
              {line.discount_minor > 0 ? (
                <div>
                  <dt className="t-label">Discount applied by the server</dt>
                  <dd>
                    <Money minor={-line.discount_minor} size="s" />
                  </dd>
                </div>
              ) : null}
              <div>
                <dt className="t-label">Tax on this line</dt>
                <dd>
                  {state.status === "TAX_PENDING" ? "Being calculated" : <Money minor={line.tax_minor} size="s" />}
                </dd>
              </div>
              <div>
                <dt className="t-label">{state.status === "TAX_PENDING" ? "Line amount before final tax" : "Line total"}</dt>
                <dd>
                  <Money minor={line.final_line_total_minor} size="s" />
                </dd>
              </div>
            </dl>
          </li>
        ))}
      </ul>

      <dl className="summary-totals">
        <div>
          <dt>Items</dt>
          <dd>
            <Money minor={state.item_subtotal_minor} size="s" />
          </dd>
        </div>
        {state.discount_minor > 0 ? (
          <div>
            <dt>Discount</dt>
            <dd>
              <Money minor={-state.discount_minor} size="s" />
            </dd>
          </div>
        ) : null}
        <div>
          <dt>Shipping</dt>
          <dd>
            <Money minor={state.shipping_minor} size="s" />
          </dd>
        </div>
        <div>
          <dt>Insurance</dt>
          <dd>
            <Money minor={state.insurance_minor} size="s" />
          </dd>
        </div>
        <div>
          <dt>Tax</dt>
          <dd>
            {state.status === "TAX_PENDING" ? (
              <span className="t-body-s t-secondary">Being calculated</span>
            ) : (
              <Money minor={state.tax_minor} size="s" />
            )}
          </dd>
        </div>
        <div className="summary-total">
          <dt>{state.status === "TAX_PENDING" ? "Amount before final tax" : stageTotalLabel(state)}</dt>
          <dd>
            <Money minor={state.total_minor} />
          </dd>
        </div>
      </dl>

      <p className="t-body-s t-secondary">
        Tax is calculated for your delivery address. The confirmed amount is recorded with your order.
      </p>

      {check.agrees ? null : (
        <div className="state state--stop" role="alert">
          <p className="state-title t-body-l">
            <StatusChip label="Stopped" tone="stop" />
            These figures do not add up.
          </p>
          <p className="state-detail">
            The itemised parts come to <Money minor={check.itemisedMinor} size="s" /> but the server
            reported a total of <Money minor={state.total_minor} size="s" />. Paying is blocked until
            that is resolved. This page cannot confirm a payment from these figures.
          </p>
        </div>
      )}
    </section>
  );
}

/**
 * The handoff.
 *
 * Core alone may publish an `approval_url`, and only while its authoritative
 * state still permits payment. The public contract validates that URL against
 * the exact PayPal approval route, so this component has exactly two honest
 * branches — hand off, or state that there is nothing to hand off to. It never
 * fabricates a destination, and it never shows a dead button dressed up as a
 * working one (Design Bible 14).
 */
interface CheckoutPresentation extends CheckoutStage {
  readonly approvalURL: string | undefined;
  readonly currentStep: "Review total" | "PayPal" | "Confirmation" | null;
}

/** The parsed core response authorizes the link; a READY quote alone does not. */
function checkoutPresentation(state: CheckoutState): CheckoutPresentation {
  const stage = checkoutStage(state.status);
  if (stage.paid) return { ...stage, approvalURL: undefined, currentStep: "Confirmation" };
  if (state.status === "EXPIRED" || state.status === "CANCELLED") {
    return { ...stage, approvalURL: undefined, currentStep: null };
  }
  if (state.status === "TAX_PENDING") {
    return { ...stage, approvalURL: undefined, currentStep: "Review total" };
  }
  if (!checkItemisedTotal(state).agrees) {
    return {
      title: "Payment is blocked",
      detail: "The itemised amounts do not match the order total. Check status before continuing.",
      paid: false,
      awaitingAuthority: true,
      approvalURL: undefined,
      currentStep: "Review total"
    };
  }
  // parseCheckoutState validates the URL and rejects links on held orders or
  // any status other than PENDING_PAYMENT. Keep the order identity mandatory.
  if (state.status === "PENDING_PAYMENT" && state.approval_url !== undefined && state.order_id !== undefined) {
    return { ...checkoutStage("READY"), approvalURL: state.approval_url, currentStep: "Review total" };
  }
  if (state.status === "READY" || state.status === "CONSUMED" || state.approval_url !== undefined) {
    return {
      title: "Preparing PayPal",
      detail: "Your total is ready. We are waiting for the server to make payment available. Payment has not been confirmed.",
      paid: false,
      awaitingAuthority: false,
      approvalURL: undefined,
      currentStep: "Review total"
    };
  }
  return { ...stage, approvalURL: undefined, currentStep: "PayPal" };
}

function stageTotalLabel(state: CheckoutState): string {
  const stage = checkoutPresentation(state);
  return stage.paid ? "Paid total" : stage.approvalURL === undefined ? "Order total" : "Total to approve";
}

function PayPalHandoff({ state }: { state: CheckoutState }): ReactNode {
  const check = checkItemisedTotal(state);
  const stage = checkoutPresentation(state);

  if (state.status === "TAX_PENDING") {
    return (
      <p className="t-body-m" role="status">
        Tax is still being calculated. The amount above is not a final total, and payment is not available yet.
      </p>
    );
  }

  if (stage.paid) {
    return (
      <p className="t-body-m" role="status">
        This order is paid. The confirmation below is the authoritative record.
      </p>
    );
  }

  if (!check.agrees) {
    return (
      <p className="t-body-m">
        Payment is blocked while the totals disagree. Check this order's status before starting another checkout.
      </p>
    );
  }

  if (stage.approvalURL !== undefined) {
    return (
      <div className="actions">
        <a
          className="btn btn--primary"
          href={stage.approvalURL}
          rel="noopener noreferrer"
          // Same tab: the return journey is a real navigation back to this
          // origin, and a popup would be blocked or lost on mobile.
        >
          Approve payment on PayPal
        </a>
        <p className="t-body-s t-secondary">
          Approve the payment on PayPal, then return here for confirmation. Your order is confirmed
          once PayPal verifies the payment.
        </p>
      </div>
    );
  }

  if (stage.awaitingAuthority) {
    return (
      <div className="state state--accent" role="status">
        <p className="state-title t-body-l">
          <StatusChip label="Confirming" tone="accent" />
          {stage.title}
        </p>
        <p className="state-detail">{stage.detail}</p>
      </div>
    );
  }

  return (
    <div className="state state--accent">
      <p className="state-title t-body-l">
        <StatusChip label="Waiting on the server" tone="accent" />
        Payment cannot be opened from this page yet.
      </p>
      <p className="state-detail">
        Your total is ready. We are waiting for a payment link before you can continue.
        Payment has not been confirmed. Check this order's status before trying another checkout.
      </p>
      <div className="state-actions">
        <a className="btn btn--secondary" href="/help">
          Ask about this order
        </a>
      </div>
    </div>
  );
}
